import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery } from "../../store/api/authApi";
import LoginForm from "./components/LoginForm";
import LoginHeader from "./components/LoginHeader";

export type LoginRole = "admin" | "tenant" | "technician" | "agent";

const Login: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const accessToken = tokenStorage.getAccessToken();
	// Only fetch profile if we have a token (skip query if no token)
	const { data: user, isLoading } = useGetProfileQuery(
		accessToken ? undefined : skipToken,
	);

	// Redirect if already authenticated
	useEffect(() => {
		if (accessToken && user && !isLoading) {
			// Get the intended destination from URL query params or location state
			const searchParams = new URLSearchParams(location.search);
			const returnUrl = searchParams.get("returnUrl");
			const redirect = searchParams.get("redirect");
			const from = (location.state as { from?: { pathname: string } })?.from
				?.pathname;

			// Priority: returnUrl > redirect > from > default role-based redirect
			if (returnUrl && returnUrl !== "/login") {
				navigate(returnUrl, { replace: true });
			} else if (redirect && redirect !== "/login") {
				navigate(redirect, { replace: true });
			} else if (from && from !== "/login") {
				navigate(from, { replace: true });
			} else {
				// Role-based default redirect
				const roleRedirects = {
					admin: "/admin-dashboard",
					tenant: "/tenant-dashboard",
					technician: "/technician-dashboard",
					agent: "/agent-dashboard",
				};
				const defaultPath = roleRedirects[user.role as keyof typeof roleRedirects] || "/";
				navigate(defaultPath, { replace: true });
			}
		}
	}, [user, isLoading, navigate, location, accessToken]);

	const searchParams = new URLSearchParams(location.search);
	const selectedRole = searchParams.get("role") as LoginRole | null;

	return (
		<div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background p-4">
			<div className="absolute inset-0 opacity-5">
				<div
					className="absolute inset-0"
					style={{
						backgroundImage:
							"url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
					}}
				/>
			</div>

			<div className="relative w-full max-w-md">
				<div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8">
					<div className="absolute -right-16 -top-16 h-32 w-32 rounded-full bg-primary/5" />
					<div className="absolute -bottom-12 -left-12 h-24 w-24 rounded-full bg-accent/5" />

					<div className="relative z-10">
						<LoginHeader selectedRole={selectedRole} />
						<LoginForm />
					</div>
				</div>

				<div className="mt-6 text-center">
					<p className="text-xs text-muted-foreground">
						Dofarahub | Copyright © {new Date().getFullYear()}
					</p>
				</div>
			</div>
		</div>
	);
};

export default Login;
